import MicroModal from 'micromodal';
import type { WidgetConfig } from '@/types/config';

/**
 * Modal options for configuration
 */
export interface ModalOptions {
  id: string;
  content: HTMLElement;
  config: WidgetConfig;
  onShow?: () => void;
  onClose?: () => void;
}

/**
 * Modal component - wraps Micromodal.js for accessible modal dialogs
 */
export class Modal {
  private modalId: string;
  private modalElement: HTMLElement | null = null;
  private options: ModalOptions;

  constructor(options: ModalOptions) {
    this.options = options;
    this.modalId = options.id;
  }

  /**
   * Create modal HTML structure
   */
  public create(): void {
    if (this.modalElement) {
      console.warn('[CaredoveWidget] Modal already exists');
      return;
    }

    const modal = document.createElement('div');
    modal.id = this.modalId;
    modal.className = 'caredove-modal micromodal-slide';
    modal.setAttribute('aria-hidden', 'true');

    const isLocked = !this.options.config.locked;
    const title = this.options.config.title || '';
    const showLink = this.options.config.showLink;

    modal.innerHTML = `
      <div class="caredove-modal__overlay" tabindex="-1" ${isLocked ? 'data-micromodal-close' : ''}>
        <div
          class="caredove-modal__container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="${this.modalId}-title"
          style="${this.options.config.width ? `width: ${this.options.config.width};` : 'width: 100%;'} max-width: 60vw;">
          <header class="caredove-modal__header">
            ${title ? `<h2 id="${this.modalId}-title" class="caredove-modal__title">${title}</h2>` : ''}
            <button class="caredove-modal__close" aria-label="Close modal" data-micromodal-close></button>
          </header>
          ${showLink ? `<div class="caredove-modal__link"></div>` : ''}
          <div class="caredove-modal__content"></div>
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    this.modalElement = modal;

    // Append user content
    const contentContainer = modal.querySelector('.caredove-modal__content');
    if (contentContainer) {
      contentContainer.appendChild(this.options.content);
    }
  }

  /**
   * Show the modal
   */
  public show(): void {
    if (!this.modalElement) {
      this.create();
    }

    try {
      MicroModal.show(this.modalId, {
        disableScroll: true,
        disableFocus: false,
        awaitOpenAnimation: true,
        awaitCloseAnimation: true,
        onShow: () => {
          this.disableTouchAction();
          this.options.onShow?.();
        },
        onClose: () => {
          this.enableTouchAction();
          this.options.onClose?.();
          this.destroy();
        },
      });
    } catch (error) {
      console.error('[CaredoveWidget] Error showing modal:', error);
    }
  }

  /**
   * Close the modal
   */
  public close(): void {
    try {
      MicroModal.close(this.modalId);
    } catch (error) {
      console.error('[CaredoveWidget] Error closing modal:', error);
    }
  }

  /**
   * Destroy the modal and clean up DOM
   */
  public destroy(): void {
    if (this.modalElement) {
      this.modalElement.remove();
      this.modalElement = null;
    }
  }

  /**
   * Get the modal element
   */
  public getElement(): HTMLElement | null {
    return this.modalElement;
  }

  /**
   * Check if modal exists in DOM
   */
  public exists(): boolean {
    return this.modalElement !== null;
  }

  /**
   * Disable touch action to prevent swipe-back on mobile
   */
  private disableTouchAction(): void {
    document.body.style.overscrollBehaviorX = 'none';
  }

  /**
   * Re-enable touch action
   */
  private enableTouchAction(): void {
    document.body.style.overscrollBehaviorX = '';
  }

  /**
   * Update modal content
   */
  public updateContent(newContent: HTMLElement): void {
    if (!this.modalElement) {
      console.warn('[CaredoveWidget] Cannot update content: modal does not exist');
      return;
    }

    const contentContainer = this.modalElement.querySelector('.caredove-modal__content');
    if (contentContainer) {
      contentContainer.innerHTML = '';
      contentContainer.appendChild(newContent);
    }
  }

  /**
   * Set link in modal header (for showLink option)
   */
  public setLink(url: string): void {
    if (!this.modalElement) return;

    const linkContainer = this.modalElement.querySelector('.caredove-modal__link');
    if (linkContainer) {
      linkContainer.innerHTML = `
        <a href="${url}" target="_blank" rel="noopener noreferrer" class="caredove-modal__link-text">
          ${url}
        </a>
      `;
    }
  }
}
